import "./time-slip-view.css";
import "./live-view.css";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLiveStatus } from "./LiveStatusContext";
import { useTour } from "./hooks/useTour";
import { useAuth } from "./AuthProvider";
import { useInstallPrompt } from "./hooks/useInstallPrompt";
import InstallPromptBanner from "./components/InstallPromptBanner";
import { getClassIndex, getOverUnder } from "./utils/raceIndex";
import msiLogo from "./assets/Micro Strategies Logo 2025_RBG_Primary Horizontal_White_1200 px.png";
import dragInsightsLogo from "./assets/drag-insights-logos-lights-white.png";

const LIVE_TOUR_STEPS = () => [
  {
    element: '.live-status-banner',
    popover: {
      title: 'Race Status',
      description: 'Shows the current race status — updates automatically in real time.',
    },
  },
  {
    element: '.live-on-deck',
    popover: {
      title: 'On Deck',
      description: 'The next pair of cars staged and ready to race.',
    },
  },
  {
    element: '.live-reconnect-button',
    popover: {
      title: 'Reconnect',
      description: 'If the live feed stops updating, tap this to force a fresh connection.',
    },
  },
];

const LiveView = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAndroidPrompt, showIOSGuide, handleInstallClick, handleDismiss, isAndroid, isIOS } = useInstallPrompt();
  const {
    raceState,
    pendingRaceState,
    connectionStatus,
    carRunCount,
    ensureActive,
    forceReconnect,
  } = useLiveStatus();

  const { startIfNew } = useTour('live', LIVE_TOUR_STEPS);

  useEffect(() => {
    ensureActive();
  }, [ensureActive]);

  useEffect(() => {
    startIfNew();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reconnecting = connectionStatus === "connecting";

  const {
    leftCarNo,   leftDriver,   leftCarClass,
    leftDialIn,  leftRt,       leftFt60,
    leftFt330,   leftft8,      leftft8Mph,
    leftft1000,  leftft4,      leftft4Mph,
    leftFirst,   leftCurrentDialIn,
    rightCarNo,  rightDriver,  rightCarClass,
    rightDialIn, rightRt,      rightFt60,
    rightFt330,  rightft8,     rightft8Mph,
    rightft1000, rightft4,     rightft4Mph,
    rightFirst,   rightCurrentDialIn,
    mov, win, category, round, timestamp,
  } = raceState;

  const leftWon     = win === "LEFT";
  const rightWon    = win === "RIGHT";
  const raceComplete = win && win !== "-";
  const hasRaceData  = timestamp && timestamp !== "-";

  const displayTime = (timestamp && timestamp !== "-")
    ? timestamp.split(" ")[1] || timestamp
    : "-";

  const hasLeftFirst  = leftFirst  && leftFirst  !== "-" && leftFirst  !== "NA";
  const hasRightFirst = rightFirst && rightFirst !== "-" && rightFirst !== "NA";

  const pending = pendingRaceState || {};

  if (connectionStatus === "no-track") {
    return (
      <div className="time-slip-container">
        <div className="live-no-track">
          <p>No track selected. Please select a track first.</p>
          <button className="tsv-button" onClick={() => navigate("/editTrackEvent")}>
            Select a Track
          </button>
        </div>
      </div>
    );
  }



  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//   useEffect(() => {
//   const disableRightClick = (e) => {
//     e.preventDefault();
//     return false;
//   };
//   document.addEventListener('contextmenu', disableRightClick);
//   return () => document.removeEventListener('contextmenu', disableRightClick);
// }, []);


// useEffect(() => {
//   const blockShortcuts = (e) => {
//     // F12
//     if (e.key === 'F12') {
//       e.preventDefault();
//       return false;
//     }
//     // Ctrl+Shift+I (Dev Tools)
//     if (e.ctrlKey && e.shiftKey && e.key === 'I') {
//       e.preventDefault();
//       return false;
//     }
//     // Ctrl+Shift+J (Console)
//     if (e.ctrlKey && e.shiftKey && e.key === 'J') {
//       e.preventDefault();
//       return false;
//     }
//     // Ctrl+Shift+C (Inspector)
//     if (e.ctrlKey && e.shiftKey && e.key === 'C') {
//       e.preventDefault();
//       return false;
//     }
//   };
  
//   document.addEventListener('keydown', blockShortcuts);
//   return () => document.removeEventListener('keydown', blockShortcuts);
// }, []);



  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////








  return (
    <div className="time-slip-container">
      {user && (
        <InstallPromptBanner
          showAndroidPrompt={showAndroidPrompt}
          showIOSGuide={showIOSGuide}
          onInstall={handleInstallClick}
          onDismiss={handleDismiss}
          isAndroid={isAndroid}
          isIOS={isIOS}
        />
      )}

      {/* ── Maintenance Banner ── */}


      {/* ── Status banner with inline metadata ── */}
      <div className={`live-status-banner ${raceComplete ? "complete" : hasRaceData ? "in-progress" : "awaiting"}`}>
        <button
          type="button"
          className="live-reconnect-button"
          onClick={forceReconnect}
          disabled={reconnecting}
          title="Force a fresh connection to the live data feed"
          aria-label={reconnecting ? "Reconnecting" : "Reconnect"}
        >
          <span className="live-reconnect-icon" aria-hidden="true">↻</span>
          <span className="live-reconnect-label">
            {reconnecting ? "Reconnecting…" : "Reconnect"}
          </span>
        </button>
        <div className="live-status-main">
          {raceComplete
            ? "Race Complete"
            : hasRaceData
            ? <><span className="live-dot-small" /> Race in Progress</>
            : "No Race in Progress"}
        </div>
        {(() => {
          const parts = [category, round, displayTime].filter(
            (v) => v && v !== "-" && v !== "NA"
          );
          return parts.length > 0 ? (
            <div className="live-status-meta">{parts.join(" · ")}</div>
          ) : null;
        })()}
      </div>

      {/* ── On Deck panel ── */}
      <div className="live-on-deck">
        <span className="live-on-deck-label">On Deck</span>
        <div className="live-on-deck-racers">
          <div className="live-on-deck-lane">
            <span className="live-on-deck-car">{pending.leftCarNo || "-"}</span>
            <span className="live-on-deck-driver">{pending.leftDriver || "-"}</span>
          </div>
          <span className="live-on-deck-vs">vs</span>
          <div className="live-on-deck-lane live-on-deck-lane--right">
            <span className="live-on-deck-car">{pending.rightCarNo || "-"}</span>
            <span className="live-on-deck-driver">{pending.rightDriver || "-"}</span>
          </div>
        </div>
          <div className="live-car-count-row">
            <span className="live-car-count-label">Cars Run</span>
            <span className="live-car-count-value">{carRunCount ?? "-"}</span>
          </div>
      </div>

      {/* ── Driver / car info ── */}
      <table className="race-data-table-driverdata">
        <tbody>
          <tr>
            <th>Car Number</th>
            <td className={leftWon  ? "winner-highlight" : ""}>{leftCarNo}</td>
            <td className={rightWon ? "winner-highlight" : ""}>{rightCarNo}</td>
          </tr>
          <tr>
            <th>Driver Name</th>
            <td className={leftWon  ? "winner-highlight" : ""}>
              {leftDriver}
              {leftWon && <span className="win-tag">WIN</span>}
            </td>
            <td className={rightWon ? "winner-highlight" : ""}>
              {rightDriver}
              {rightWon && <span className="win-tag">WIN</span>}
            </td>
          </tr>
          <tr>
            <th>Class</th>
            <td className={leftWon  ? "winner-highlight" : ""}>{leftCarClass}</td>
            <td className={rightWon ? "winner-highlight" : ""}>{rightCarClass}</td>
          </tr>
          <tr>
            <th>Index</th>
            <td className={leftWon  ? "winner-highlight" : ""}>
              {getClassIndex(leftCarClass, timestamp, leftft4) || "-"}
            </td>
            <td className={rightWon ? "winner-highlight" : ""}>
              {getClassIndex(rightCarClass, timestamp, rightft4) || "-"}
            </td>
          </tr>
          <tr>
            <th>Over / Under</th>
            <td className={leftWon  ? "winner-highlight" : ""}>
              {getOverUnder(leftCarClass, timestamp, leftft4, leftft8) || "-"}
            </td>
            <td className={rightWon ? "winner-highlight" : ""}>
              {getOverUnder(rightCarClass, timestamp, rightft4, rightft8) || "-"}
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Race metrics ── */}
      <table className="race-data-table">
        <tbody>
          <tr>
            <th>Dial-in Time</th>
            <td>{leftCurrentDialIn}</td>
            <td>{rightCurrentDialIn}</td>
          </tr>
          <tr>
            <th>Reaction Time</th>
            <td>{raceComplete ? leftRt    : "-"}</td>
            <td>{raceComplete ? rightRt   : "-"}</td>
          </tr>
          <tr>
            <th>60&apos; Time</th>
            <td>{raceComplete ? leftFt60  : "-"}</td>
            <td>{raceComplete ? rightFt60 : "-"}</td>
          </tr>
          <tr>
            <th>330&apos; Time</th>
            <td>{raceComplete ? (leftFt330  && leftFt330  !== "-" ? leftFt330  : "-") : "-"}</td>
            <td>{raceComplete ? (rightFt330 && rightFt330 !== "-" ? rightFt330 : "-") : "-"}</td>
          </tr>
          <tr>
            <th>1/8 Mile ET</th>
            <td>{raceComplete ? leftft8    : "-"}</td>
            <td>{raceComplete ? rightft8   : "-"}</td>
          </tr>
          <tr>
            <th>1/8 Mile (MPH)</th>
            <td>{raceComplete ? leftft8Mph  : "-"}</td>
            <td>{raceComplete ? rightft8Mph : "-"}</td>
          </tr>
          <tr>
            <th>1000&apos; Time</th>
            <td>{raceComplete ? (leftft1000  && leftft1000  !== "-" ? leftft1000  : "-") : "-"}</td>
            <td>{raceComplete ? (rightft1000 && rightft1000 !== "-" ? rightft1000 : "-") : "-"}</td>
          </tr>
          <tr>
            <th>1/4 Mile ET</th>
            <td>{raceComplete ? leftft4    : "-"}</td>
            <td>{raceComplete ? rightft4   : "-"}</td>
          </tr>
          <tr>
            <th>1/4 Mile (MPH)</th>
            <td>{raceComplete ? leftft4Mph  : "-"}</td>
            <td>{raceComplete ? rightft4Mph : "-"}</td>
          </tr>
        </tbody>
      </table>

      {/* ── First light / MOV ── */}
      <table className="race-data-table-last">
        <tbody>
          <tr>
            <th>
              {hasLeftFirst && !hasRightFirst
                ? "Left 1st"
                : !hasLeftFirst && hasRightFirst
                ? "Right 1st"
                : "1st"}
            </th>
            <td>{hasLeftFirst ? leftFirst : hasRightFirst ? rightFirst : "-"}</td>
          </tr>
          <tr>
            <th>Margin of Victory (MOV)</th>
            <td>{raceComplete ? mov : "-"}</td>
          </tr>
        </tbody>
      </table>

      <footer className="footer">
        <div className="footer-logo-data" />
        <div className="footer-logo-container">
          <img src={msiLogo}          alt="Micro Strategies Logo" className="footer-logo" />
          <img src={dragInsightsLogo} alt="Drag Insights Logo"    className="footer-logo" />
        </div>
      </footer>
    </div>
  );
};

export default LiveView;
